import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../../../utils/apiClient';
import HALSearchInput, { HALSearchQuery } from '../../../../../features/hal/components/HALSearchInput';

interface HALModalProps {
    isVisible: boolean;
    onClose: () => void;
    onSelect: (ressource: any) => void;
    initialSearchText?: string;
}

interface HALMetadata {
    abstract?: string;
    keywords?: string[];
    authors?: string[];
    journal?: {
        title?: string;
        publisher?: string;
        issn?: string;
        volume?: string;
        issue?: string;
        pages?: string;
    };
    publication?: {
        type: string;
        subType?: string;
        date: string;
        language: string;
        domain: string[];
    };
}

interface Ressource {
    _id: string;
    titre: string;
    source: {
        nom: string;
        identifiant: string;
        url: string;
    };
    dateImportation: string;
    halMetadata?: HALMetadata;
}

interface ApiResponse {
    ressources: Ressource[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

const HALModal: React.FC<HALModalProps> = ({ isVisible, onClose, onSelect, initialSearchText }) => {
    const [ressources, setRessources] = useState<Ressource[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [currentSearch, setCurrentSearch] = useState<HALSearchQuery>({ value: '', field: 'q' });

    // Initialiser la recherche avec le texte sélectionné
    useEffect(() => {
        if (isVisible && initialSearchText) {
            const initialQuery: HALSearchQuery = { value: initialSearchText, field: 'q' };
            setCurrentSearch(initialQuery);
            fetchRessources(1, initialQuery);
        } else if (isVisible) {
            // Si pas de texte, on peut soit charger les résultats par défaut, soit attendre une recherche
            // Pour l'instant, on attend une recherche ou on charge par défaut
            fetchRessources(1, { value: '', field: 'q' });
        } else {
            // Reset quand on ferme
            setRessources([]);
            setPage(1);
            setCurrentSearch({ value: '', field: 'q' });
        }
    }, [isVisible, initialSearchText]);

    const fetchRessources = useCallback(async (pageNum: number, search: HALSearchQuery) => {
        setLoading(true);
        try {
            let path = `/ressources?source=HAL&page=${pageNum}&limit=10`;

            if (search.value) {
                const val = encodeURIComponent(search.value);
                if (search.field === 'q') {
                    path += `&q=${val}`;
                } else {
                    path += `&${search.field}=${val}`;
                }
            }

            const response = await apiClient(path);

            if (!response.ok) {
                throw new Error('Erreur réseau');
            }

            const data: ApiResponse = await response.json();

            if (pageNum === 1) {
                setRessources(data.ressources);
            } else {
                setRessources(prev => [...prev, ...data.ressources]);
            }

            setHasMore(data.pagination.page < data.pagination.pages);
        } catch (error) {
            console.error("Erreur lors du chargement des ressources HAL:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = (query: HALSearchQuery) => {
        setCurrentSearch(query);
        setPage(1);
        fetchRessources(1, query);
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchRessources(nextPage, currentSearch);
        }
    };

    if (!isVisible) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-20 z-30 transition-opacity duration-300"
            onClick={onClose}
        >
            <div
                className="legifrance-sidebar fixed inset-y-0 right-0 w-[500px] bg-mcdm-primary-100 dark:bg-mcdm-third-400 shadow-lg shadow-inner z-30 overflow-y-auto transform transition-transform duration-300 flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-mcdm-primary-100 dark:bg-mcdm-third-400 p-4 border-b border-mcdm-primary-200 dark:border-mcdm-primary-300 z-20">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100">
                            Recherche HAL
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-mcdm-third-500 dark:text-mcdm-primary-100"
                            aria-label="Fermer"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <HALSearchInput
                        onSearch={handleSearch}
                        initialValue={currentSearch.field === 'q' ? currentSearch.value : ''}
                    />
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {loading && page === 1 ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mcdm-secondary-400"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {ressources.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    Aucun résultat trouvé
                                </div>
                            ) : (
                                ressources.map((ressource) => (
                                    <div
                                        key={ressource._id}
                                        onClick={() => onSelect(ressource)}
                                        className="bg-white dark:bg-mcdm-third-300 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-mcdm-secondary-400 dark:hover:border-mcdm-secondary-400 transition-colors"
                                    >
                                        <h3 className="font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2 line-clamp-2">
                                            {ressource.titre}
                                        </h3>
                                        <div className="flex justify-between items-start text-xs text-gray-500 dark:text-gray-400">
                                            <span>
                                                {ressource.halMetadata?.publication?.date ? new Date(ressource.halMetadata.publication.date).getFullYear() : ''}
                                            </span>
                                            <span className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded-full">
                                                {ressource.halMetadata?.publication?.type || 'Publication'}
                                            </span>
                                        </div>
                                        {ressource.halMetadata?.authors && (
                                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-1">
                                                {ressource.halMetadata.authors.slice(0, 2).join(', ')}
                                                {ressource.halMetadata.authors.length > 2 && ' et al.'}
                                            </p>
                                        )}
                                    </div>
                                ))
                            )}

                            {hasMore && (
                                <div className="text-center pt-4">
                                    <button
                                        onClick={loadMore}
                                        disabled={loading}
                                        className="text-sm text-mcdm-secondary-500 hover:text-mcdm-secondary-600 font-medium disabled:opacity-50"
                                    >
                                        {loading ? 'Chargement...' : 'Charger plus'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HALModal;
